import { IDT } from '@ff-os/pdp-shared-redux/IDT';
import { displayModes, languages } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { findLanguage } from '@ff-os/shared-translations/localization';
import { logError } from '@ff-os/shared-utils/logging';

export const reportConfigurationErrors = (options: IDT.Options) => {

    const errors = [];

    errors.push(...validateMode(options));
    errors.push(...validateLanguage(options));
    errors.push(...validateContainers(options));
    errors.push(...validateOpenOptions(options));
    errors.push(...validateEvents(options));

    if (errors.length) {
        logError(
            'Invalid IDT configuration. Refer to the docs for details on how to '
            + 'correctly configure IDT in your site. Details:\n\n * '
            + errors.join('\n\n * '));
    }
};

const validateMode = (options: IDT.Options): string[] => {

    if (!displayModes.includes(options.mode)) {
        return [
            `Invalid display mode "${options.mode}". The "mode" property must be set to `
            + `one of the following values: "${displayModes.join('", "')}"`
        ];
    }

    return [];
};

const validateLanguage = (options: IDT.Options): string[] => {

    if (!findLanguage(languages, options.language)) {
        return [
            `Invalid language "${options.language}". The "language" property must be set `
            + `to one of the following values: "${languages.join('", "')}"`
        ];
    }

    return [];
};

const validateContainers = (options: IDT.Options): string[] => {

    if (!options.containers) {
        return [
            'No containers supplied. The IDT options must include a property named '
            + '"containers" which is set to an object that contains the containers for '
            + 'the IDT UI (properties "designer" and "preview").'
        ];
    }

    const designerKey = 'designer' as keyof IDT.Containers;
    const modalKey = 'modal' as keyof IDT.Containers;

    if (options.containers[designerKey] && options.containers[modalKey]) {
        return ['"designer" and "modal" containers cannot be used simultaneously.'];
    }

    if (options.containers[modalKey] && options.mode === 'inline') {
        return [
            'Display mode "inline" and "modal" container cannot be used simultaneously. '
            + 'The "modal" container can only be used in conjunction with "window" mode.'
        ];
    }

    if (!options.containers[designerKey] && !options.containers[modalKey]) {
        return [
            `No valid "${designerKey}" or "${modalKey}" container supplied. `
            + 'The IDT options must include one of the following properties: '
            + `"containers: { ${designerKey}: ___ }" or `
            + `"containers: { ${modalKey}: ___ }". `
            + 'These must be set to an HTMLElement object (not a query selector). '
            + 'If the options do appear to specify a container, ensure the selector '
            + 'did not return null.'
        ];
    }

    return [];
};

const validateOpenOptions = (options: IDT.Options): string[] => {

    if (!options.open) {
        return [];
    }

    const errors: string[] = [];

    if (!options.open.authorize) {
        errors.push(
            'No project grant supplied. The IDT options must include a property '
            + '"open: { authorize: ___ }" when opening a project. This property must be '
            + 'set to a project grant obtained from the API.');
    }

    if (typeof options.open.refresh !== 'function') {
        errors.push(
            'No valid "refresh" callback supplied. The IDT options must include a property '
            + '"open: { refresh: ___ }", which must be set to a function that retrieves '
            + 'a new project grant from the API. This function should return a promise '
            + 'that resolves to a string (the project grant).');
    }

    return errors;
};

const validateEvents = (options: IDT.Options): string[] => {

    if (!options.events) {
        return [
            'No event callbacks supplied. The IDT options must include a property named '
            + '"events" which is set to an object that contains the callbacks for IDT.'
        ];
    }

    const errors: string[] = [];

    const formatError = (name: keyof IDT.Events) => `No valid "${name}" callback supplied. The IDT `
        + `options must include a property "events: { ${name}: ___ }" which must be set `
        + 'to a function that handles the event.';

    for (const key of ['order'] as (keyof IDT.Events)[]) {
        const callback = options.events[key];
        if (typeof callback !== 'function') {
            errors.push(formatError(key));
        }
    }

    return errors;
};